import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Select,
  Button,
  Table,
  Upload,
  message,
  Modal,
  Space,
  Typography,
  Card,
  Divider,
  Descriptions,
  Tag,
  Radio,
  Cascader,
  DatePicker,
} from "antd";
import {
  UploadOutlined,
  DownloadOutlined,
  EyeOutlined,
  DeleteOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import httpUtil from "../../../../utils/httpUtil";
import dayjs from "dayjs";
import "dayjs/locale/zh-cn"; // 导入dayjs中文语言包
import locale from "antd/es/date-picker/locale/zh_CN";
// 1. 首先确保导入dayjs插件
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

// 扩展dayjs功能
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

dayjs.locale("zh-cn"); // 设置dayjs使用中文

const { RangePicker } = DatePicker;
const { Option } = Select;

const statusExplanation = {
  title: "评价流程说明",
  content: (
    <div>
      <p>
        <strong>1. 学生编辑草稿状态记录</strong> -
        学生可以创建和修改草稿状态的评价记录
      </p>
      <p>
        <strong>2. 学生提交草稿记录</strong> -
        学生提交后记录变为"已提交"状态，等待老师审核
      </p>
      <p>
        <strong>3. 老师审核记录</strong> - 老师审核通过后记录变为"已审核"状态
      </p>
      <p>
        <strong>4. 公示记录</strong> - 审核通过的评价将进入公示期
      </p>
      <p>
        <strong>5. 评价已完成</strong> -
        公示期结束后评价流程完成，记录变为"已完成"状态
      </p>
      <Divider />
      <p>
        <Tag color="gray">草稿</Tag> - 学生可编辑的草稿状态
      </p>
      <p>
        <Tag color="orange">已提交</Tag> - 等待老师审核
      </p>
      <p>
        <Tag color="blue">已审核</Tag> - 老师已审核通过
      </p>
      <p>
        <Tag color="red">已驳回</Tag> - 老师审核未通过
      </p>
      <p>
        <Tag color="purple">申诉中</Tag> - 学生对评价有异议，正在申诉
      </p>
      <p>
        <Tag color="green">已完成</Tag> - 评价记录流程已完成
      </p>
    </div>
  ),
};

export const ApplicationMaterials = ({ userInfo }) => {
  const [form] = Form.useForm();
  const [modalForm] = Form.useForm();
  const [tableLoading, setTableLoading] = useState(false);
  const [comfirmLoading, setComfirmLoading] = useState(false);
  const [applications, setApplications] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [selectedApp, setSelectedApp] = useState(null);
  const [open, setOpen] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [indicators, setIndicators] = useState([]); // 评价指标数据
  const [searchForm] = Form.useForm();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [showExplanation, setShowExplanation] = useState(true);

  // 获取当前学生的申请记录
  const getApplications = () => {
    setTableLoading(true);
    setPagination({ current: 1, pageSize: 10 });
    const parmas = { student_id: userInfo.username };
    httpUtil
      .getEvaluationsByStudent(parmas)
      .then((res) => {
        // console.log(res);
        if (res.success) {
          setApplications(res.data);
        } else {
          message.error(res.error || "获取申请记录失败");
        }
      })
      .finally(() => setTableLoading(false));
  };

  const getIndicators = () => {
    const parmas = {
      applicant_type: userInfo.role,
    };
    httpUtil.getAllIndicators(parmas).then((res) => {
      // console.log("评价指标",res);
      if (res.success) {
        setIndicators(formatIndicators(res.data));
      }
    });
  };

  useEffect(() => {
    getIndicators();
    getApplications();
  }, []);

  // 格式化指标数据
  const formatIndicators = (data) => {
    const newTree = data.map((item) => ({
      value: item.indicator_id,
      label: item.indicator_name,
      // 存储原始指标信息
      rawData: {
        indicator_id: item.indicator_id,
        indicator_name: item.indicator_name,
      },
      children: item.children.map((child) => ({
        value: child.indicator_id,
        label: child.indicator_name,
        // 存储子指标原始信息
        rawData: {
          indicator_id: child.indicator_id,
          indicator_name: child.indicator_name,
        },
        ...child, // 保留其他属性
      })),
    }));
    return newTree;
  };

  // 文件上传前的校验
  const beforeUpload = (file) => {
    // 文件类型和大小验证
    const isAllowedType = [
      "image/jpeg",
      "image/png",
      "application/pdf",
    ].includes(file.type);
    const isLt5M = file.size / 1024 / 1024 < 5;

    if (!isAllowedType) {
      message.error("仅支持JPG/PNG/PDF格式");
      return Upload.LIST_IGNORE;
    }
    if (!isLt5M) {
      message.error("文件大小不能超过5MB");
      return Upload.LIST_IGNORE;
    }

    setFileList((prev) => [...prev, file]);
    return false; // 手动上传
  };

  //移除文件
  const onRemove = (file) => {
    setFileList((prev) => prev.filter((f) => f.uid !== file.uid));
  };

  // 文件上传处理
  const handleUploadChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  // 预览文件
  const handlePreview = (file) => {
    setPreviewFile(file);
    setPreviewVisible(true);
  };

  // 打开添加申请Modal
  const showModal = () => {
    setOpen(true);
  };

  // 关闭Modal
  const handleCancel = () => {
    modalForm.resetFields();
    setFileList([]);
    setOpen(false);
  };

  // 提交Modal表单
  const handleOk = () => {
    setComfirmLoading(true);
    modalForm.submit();
  };

  // 提交申请
  const handleSubmit = async (values) => {
    // 添加申请时间（使用当前时间）
    const applicationDate = new Date().toISOString();
    const selectedNodes = modalForm.getFieldValue("indicator");
    const selectedIndicator = indicators
      .find((item) => item.value === selectedNodes[0])
      ?.children?.find((child) => child.value === selectedNodes[1]);

    const formData = new FormData();
    formData.append("comment", values.description);
    formData.append("indicator_id", values.indicator[1]);
    formData.append("indicator_name", selectedIndicator.rawData.indicator_name);
    formData.append("student_id", userInfo.username);
    formData.append("student_name", userInfo.full_name);
    formData.append("evaluator_id", values.evaluator_id); // 添加评价人ID
    formData.append("evaluator_name", values.evaluator_name); // 添加评价人姓名
    formData.append("state", "草稿"); // 添加评价人姓名
    formData.append("evaluation_date", applicationDate);

    // 添加所有文件
    fileList.forEach((file) => {
      formData.append("files", file.originFileObj);
    });
    // 显示正在登录中的消息
    let hideLoading = message.loading('正在提交中...', 0);
    
    try {
      httpUtil.submitApplication(formData).then((res) => {
        hideLoading();
        if (res.success) {
          message.success(`提交成功，共上传${res.data.file_count}个文件`);
          modalForm.resetFields();
          setFileList([]);
          handleCancel();
          getApplications();
        } else {
          message.error(res.error || "提交失败");
        }
        setComfirmLoading(false);
      });
    } catch (err) {
      hideLoading();
      setComfirmLoading(false);
      message.error("网络错误");
    } 
  };

  // 查看申请详情
  const showApplicationDetail = (app) => {
    // 确保material是解析后的对象
    let material = {};
    try {
      material =
        app.material && typeof app.material === "string"
          ? JSON.parse(app.material)
          : app.material || {};
    } catch (e) {
      console.error("解析材料失败:", e);
      material = {};
    }

    const application = {
      ...app,
      material,
    };
    setSelectedApp(application);
  };

  // 删除申请记录
  const deleteApplication = (record_id) => {
    Modal.confirm({
      title: "确认删除申请记录?",
      content: "删除后将无法恢复",
      okText: "确认",
      cancelText: "取消",
      onOk: () => {
        const params = {
          record_id: record_id,
          student_id: userInfo.username,
        };
        httpUtil.deleteDraftEvaluation(params).then((res) => {
          if (res.success) {
            message.success("删除成功!");
            setApplications((prev) =>
              prev.filter((app) => app.record_id !== record_id)
            );
          } else {
            message.error(res.error || "删除失败");
          }
        });
      },
    });
  };

  // 提交草稿申请
  const submitDraftApplication = async (record_id) => {
    Modal.confirm({
      title: "确认提交申请?",
      content: "提交后将无法修改，请确认材料完整",
      okText: "确认提交",
      cancelText: "取消",
      onOk: async () => {
        try {
          const res = await httpUtil.submitDraftApplication({
            record_id,
            student_id: userInfo.username,
          });

          if (res.success) {
            message.success("申请提交成功");
            getApplications(); // 刷新列表
          } else {
            message.error(res.error || "提交失败");
          }
        } catch (err) {
          message.error("网络错误，提交失败");
        }
      },
    });
  };

  // 申请记录表格列
  const columns = [
    {
      title: "评价ID",
      dataIndex: "record_id",
      key: "record_id",
      width: 100,
      align: "center",
    },
    {
      title: "学生姓名",
      dataIndex: "student_name",
      key: "student_name",
      width: 100,
      align: "center",
    },
    {
      title: "申请类型",
      dataIndex: "indicator_name",
      key: "indicator_name",
      width: 120,
      align: "center",
    },
    {
      title: "申请时间",
      dataIndex: "evaluation_date",
      key: "evaluation_date",
      width: 150,
      align: "center",
      render: (text) => (text ? new Date(text).toLocaleDateString() : "-"),
    },
    {
      title: "评价人",
      dataIndex: "evaluator_name",
      key: "evaluator_name",
      width: 120,
      align: "center",
    },
    {
      title: "申请状态",
      dataIndex: "state",
      key: "state",
      width: 120,
      align: "center",
      render: (state) => {
        const colorMap = {
          已提交: "orange",
          已审核: "blue",
          已完成: "green",
          已驳回: "red",
          草稿: "gray",
          申诉中: "purple", // 新增申诉中状态
        };
        return <Tag color={colorMap[state]}>{state}</Tag>;
      },
    },
    {
      title: "申请备注",
      dataIndex: "comment",
      key: "comment",
      width: 120,
      ellipsis: true,
      render: (text) => (
        <div style={{ width: 120, overflow: "hidden" }}>
          <Typography.Text ellipsis={{ tooltip: text || "暂无评语" }}>
            {text || "暂无评语"}
          </Typography.Text>
        </div>
      ),
    },
    {
      title: "操作",
      key: "action",
      width: 150,
      align: "center",
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => showApplicationDetail(record)}
          >
            详情
          </Button>
          {record.state === "草稿" && (
            <>
              <Button
                type="link"
                onClick={() => submitDraftApplication(record.record_id)}
              >
                提交
              </Button>
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                onClick={() => deleteApplication(record.record_id)}
              >
                撤回
              </Button>
            </>
          )}
        </Space>
      ),
    },
  ];

  // 搜索处理函数
  const handleSearch = (values) => {
    setTableLoading(true);

    const params = { student_id: userInfo.username };

    httpUtil
      .getEvaluationsByStudent(params)
      .then((res) => {
        if (res.success) {
          setPagination({ current: 1, pageSize: 10 });
          let filteredData = res.data;
          // 申请类型ID筛选（精确匹配）
          if (values.indicator_id) {
            filteredData = filteredData.filter(
              (item) => item.indicator_id === values.indicator_id
            );
          }

          // 评价人ID筛选（模糊匹配）
          if (values.evaluator_id) {
            filteredData = filteredData.filter(
              (item) =>
                item.evaluator_id &&
                item.evaluator_id.includes(values.evaluator_id)
            );
          }

          // 申请状态筛选（精确匹配）
          if (values.state) {
            filteredData = filteredData.filter(
              (item) => item.state === values.state
            );
          }

          // 申请时间范围筛选（精确到月份）
          if (values.dateRange && values.dateRange.length === 2) {
            const [start, end] = values.dateRange;

            // 确保日期是dayjs对象
            const startDate = dayjs(start);
            const endDate = dayjs(end);

            filteredData = filteredData.filter((item) => {
              if (!item.evaluation_date) return false;

              const itemDate = dayjs(item.evaluation_date);

              // 添加日期有效性检查
              if (
                !itemDate.isValid() ||
                !startDate.isValid() ||
                !endDate.isValid()
              ) {
                console.error("Invalid date detected");
                return false;
              }

              return (
                itemDate.isSameOrAfter(startDate, "month") &&
                itemDate.isSameOrBefore(endDate, "month")
              );
            });
          }
          setApplications(filteredData);
        } else {
          message.error(res.error || "搜索失败");
        }
      })
      .finally(() => setTableLoading(false));
  };

  // 重置搜索
  const handleReset = () => {
    searchForm.resetFields();
    setPagination({ current: 1, pageSize: 10 });
    getApplications(); // 重新获取原始数据
  };

  return (
    <div style={{ padding: 24 }}>
      {/* 状态说明Modal */}
      <Modal
        title={statusExplanation.title}
        open={showExplanation}
        onCancel={() => setShowExplanation(false)}
        footer={[
          <Button
            key="ok"
            type="primary"
            onClick={() => setShowExplanation(false)}
          >
            我知道了
          </Button>,
        ]}
        width={700}
      >
        {statusExplanation.content}
      </Modal>

      <Card
        title="我的申请记录"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={showModal}>
            添加申请
          </Button>
        }
        style={{ height: "70vh" }}
      >
        <Form
          form={searchForm}
          layout="inline"
          onFinish={handleSearch}
          style={{ marginBottom: 16 }}
        >
          <Form.Item name="indicator_id" label="申请类型ID">
            <Input
              placeholder="请输入申请类型ID"
              allowClear
              style={{ width: 145 }}
            />
          </Form.Item>

          <Form.Item name="evaluator_id" label="评价人ID">
            <Input
              placeholder="请输入评价人ID"
              allowClear
              style={{ width: 140 }}
            />
          </Form.Item>

          <Form.Item name="state" label="申请状态">
            <Select placeholder="请选择状态" allowClear style={{ width: 120 }}>
              <Option value="草稿">草稿</Option>
              <Option value="已提交">已提交</Option>
              <Option value="已审核">已审核</Option>
              <Option value="已驳回">已驳回</Option>
              <Option value="申诉中">申诉中</Option>
              <Option value="已完成">已完成</Option>
            </Select>
          </Form.Item>

          <Form.Item name="dateRange" label="申请时间">
            <RangePicker
              style={{ width: 220 }}
              format="YYYY-MM" // 显示年月格式
              locale={locale}
              picker="month"
              disabledDate={(current) =>
                current && current > dayjs().endOf("month")
              }
            />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit">
              搜索
            </Button>
            <Button style={{ marginLeft: 8 }} onClick={handleReset}>
              重置
            </Button>
          </Form.Item>
        </Form>
        <Table
          columns={columns}
          dataSource={applications}
          rowKey="record_id"
          loading={tableLoading}
          style={{ maxHeight: "70vh" }}
          scroll={{ y: "calc(70vh - 180px)", x: "max-content" }}
          sticky={{
            offsetHeader: 0, // 固定表头
          }}
          size="small" // 紧凑型表格
          pagination={{
            ...pagination,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            onChange: (page, pageSize) => {
              setPagination({ current: page, pageSize });
            },
          }}
        />
      </Card>

      {/* 添加申请Modal */}
      <Modal
        title="提交新申请"
        open={open}
        onOk={handleOk}
        onCancel={handleCancel}
        width={800}
        okText="提交"
        cancelText="取消"
        confirmLoading={comfirmLoading}
      >
        <Form form={modalForm} layout="vertical" onFinish={handleSubmit}  >
          <Form.Item
            label="选择评价指标"
            name="indicator"
            rules={[
              {
                required: true,
                validator: (_, value) =>
                  value?.length === 2
                    ? Promise.resolve()
                    : Promise.reject("请先选择一级指标，再选择二级指标"),
              },
            ]}
          >
            <Cascader
              options={indicators}
              placeholder="请选择评价指标"
              changeOnSelect={false}
              expandTrigger="hover"
              displayRender={(labels, selectedOptions) => (
                <span>
                  {selectedOptions[0]?.label} / {selectedOptions[1]?.label}
                </span>
              )}
              fieldNames={{
                label: 'label',
                value: 'value',
                children: 'children'
              }}
            />
          </Form.Item>
          <Form.Item label="评价人信息">
            <Input.Group compact style={{ display: "flex" }}>
              <Form.Item
                name="evaluator_id"
                style={{ flex: 1, marginBottom: 0 }}
                rules={[{ required: true, message: "请输入评价人ID" }]}
              >
                <Input placeholder="评价人ID" style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item
                name="evaluator_name"
                style={{ flex: 1, marginBottom: 0 }}
                rules={[{ required: true, message: "请输入评价人姓名" }]}
              >
                <Input placeholder="评价人姓名" style={{ width: "100%" }} />
              </Form.Item>
            </Input.Group>
          </Form.Item>
          <Form.Item
            name="description"
            label="申请备注"
            rules={[{ required: true, message: "请输入申请备注" }]}
          >
            <Input.TextArea rows={4} placeholder="请详细描述您的申请备注" />
          </Form.Item>

          <Form.Item label="申请时间">
            <Input value={new Date().toLocaleString()} disabled />
          </Form.Item>

          <Form.Item
            label="上传材料"
            name="files"
            rules={[{ required: false, message: "请上传申请材料" }]}
            extra="支持JPG/PNG格式，单文件不超过2MB，最多上传5个文件"
          >
            <Upload
              fileList={fileList}
              onChange={handleUploadChange}
              onPreview={handlePreview}
              onRemove={onRemove}
              beforeUpload={beforeUpload}
              accept="image/*"
              listType="picture-card"
              maxCount={5}
              disabled={comfirmLoading}
            >
              {fileList.length >= 5 ? null : (
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>上传图片</div>
                </div>
              )}
            </Upload>
          </Form.Item>
        </Form>
      </Modal>

      {/* 申请详情Modal */}
      <Modal
        title="申请详情"
        open={!!selectedApp}
        footer={null}
        onCancel={() => setSelectedApp(null)}
        width={800}
      >
        {selectedApp && (
          <>
            <Descriptions bordered column={1}>
              <Descriptions.Item label="评价ID">
                {selectedApp.record_id}
              </Descriptions.Item>
              <Descriptions.Item label="学生姓名">
                {selectedApp.student_name || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="申请类型">
                {selectedApp.indicator_name || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="申请时间">
                {selectedApp.evaluation_date
                  ? new Date(selectedApp.evaluation_date).toLocaleString()
                  : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="评价人">
                {selectedApp.evaluator_name || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="申请状态">
                <Tag
                  color={
                    selectedApp.state === "已提交"
                      ? "orange"
                      : selectedApp.state === "已审核"
                      ? "blue"
                      : selectedApp.state === "已驳回"
                      ? "red"
                      : selectedApp.state === "申诉中"
                      ? "purple"
                      : selectedApp.state === "已完成"
                      ? "green"
                      : "gray"
                  }
                >
                  {selectedApp.state}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="评分">
                {selectedApp.score || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="申请备注">
                {selectedApp.comment || "暂无评语"}
              </Descriptions.Item>
            </Descriptions>

            {selectedApp.material && (
              <>
                <Divider orientation="left">申请材料</Divider>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "16px" }}>
                  {selectedApp.material.files?.map((file, index) => (
                    <Card
                      key={index}
                      size="small"
                      style={{ width: 200 }}
                      cover={
                        file.type.includes("image") ? (
                          <img
                            alt={file.name}
                            src={`data:${file.type};base64,${file.data}`}
                            style={{ height: 160, objectFit: "contain" }}
                            onClick={() => {
                              setPreviewFile({
                                uid: index,
                                name: file.name,
                                url: `data:${file.type};base64,${file.data}`,
                              });
                              setPreviewVisible(true);
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              height: 160,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              background: "#f0f0f0",
                            }}
                          >
                            <DownloadOutlined style={{ fontSize: 32 }} />
                          </div>
                        )
                      }
                      actions={[
                        <Button
                          type="link"
                          icon={<EyeOutlined />}
                          onClick={() => {
                            setPreviewFile({
                              uid: index,
                              name: file.name,
                              url: `data:${file.type};base64,${file.data}`,
                            });
                            setPreviewVisible(true);
                          }}
                        >
                          预览
                        </Button>,
                      ]}
                    >
                      <Card.Meta
                        title={file.name}
                        description={`${(file.size / 1024).toFixed(1)} KB`}
                      />
                    </Card>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </Modal>

      {/* 文件预览Modal */}
      <Modal
        open={previewVisible}
        title={previewFile?.name || "文件预览"}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        width="80%"
        style={{ top: 20 }}
      >
        {previewFile?.url &&
          (previewFile.url.includes("image") ? (
            <img
              alt={previewFile.name}
              src={previewFile.url}
              style={{ width: "100%", maxHeight: "70vh", objectFit: "contain" }}
            />
          ) : (
            <iframe
              title="文件预览"
              src={previewFile.url}
              style={{ width: "100%", height: "70vh", border: "none" }}
            />
          ))}
      </Modal>
    </div>
  );
};
